/**
 * The staff session token.
 *
 * Background. This app authenticates in its own layer and then talks to
 * PostgREST with the publishable key, which ships in the JS bundle every
 * visitor downloads. The database therefore could not tell a logged-in nurse
 * from a stranger, and patient_cases — 1,048 records, a name on all of them,
 * NRIC and phone on 24 — was readable by anyone who looked.
 *
 * The server (/api/app-session, issue_staff_session, migration 20260930100000)
 * now issues an opaque token, of which
 * the database stores only the SHA-256 hash. Every patient_cases read and write
 * goes through a SECURITY DEFINER *_by_session function that takes it. This
 * module is where the token lives on the client.
 *
 * It is kept apart from authService so that services which need the token do
 * not have to import the auth layer and create an import cycle.
 */

const TOKEN_KEY = 'staffSessionToken';
const EXPIRES_KEY = 'staffSessionExpiresAt';

export const setStaffSession = (token: string, expiresAt: string | null): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    if (expiresAt) {
      localStorage.setItem(EXPIRES_KEY, expiresAt);
    }
  } catch (storageError) {
    // Private browsing, or storage full. The app still works for this tab;
    // a reload will ask for the password again.
    console.warn('Failed to persist the staff session token:', storageError);
  }
};

export const clearStaffSession = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRES_KEY);
  } catch (storageError) {
    console.warn('Failed to clear the staff session token:', storageError);
  }
};

export const getStaffSessionToken = (): string | null => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;

    // Expiry is enforced server-side; this only saves a doomed round trip.
    const expiresAt = localStorage.getItem(EXPIRES_KEY);
    if (expiresAt && Date.parse(expiresAt) <= Date.now()) {
      clearStaffSession();
      return null;
    }

    return token;
  } catch {
    return null;
  }
};

/**
 * True while this browser has no token — because the session expired, or
 * because the database has no staff sessions yet, or the browser is
 * still carrying an old localStorage `userSession`.
 *
 * The patient services fall back to the direct table path in that case. That
 * fallback is what lets the migrations and the deploy land in either order
 * without a broken morning; it stops working by itself the moment Phase 3
 * revokes anon on the table, which is the point at which it should be deleted.
 */
export const hasStaffSession = (): boolean => getStaffSessionToken() !== null;

/**
 * `invalid_session` is raised by require_staff_session with SQLSTATE 28000.
 * PostgREST surfaces it as the message, so match on both.
 */
export const isInvalidSessionError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { code?: string; message?: string; details?: string };
  return (
    candidate.code === '28000' ||
    `${candidate.message ?? ''} ${candidate.details ?? ''}`.includes('invalid_session')
  );
};

/** Fired when the server rejects the token, so AuthProvider can sign the user out. */
export const STAFF_SESSION_INVALID_EVENT = 'staff-session-invalid';

/**
 * A session the server has rejected is worse than no session: every later query
 * fails, and PatientsView reads a failed case query as "records unavailable"
 * and quietly substitutes preview data — a screen that looks like it is working
 * and is showing the wrong cases. So drop the session and tell the auth layer,
 * which returns the app to the login screen.
 */
export const handleInvalidSession = (): void => {
  clearStaffSession();
  try {
    localStorage.removeItem('userSession');
  } catch {
    /* nothing useful to do */
  }

  try {
    window.dispatchEvent(new CustomEvent(STAFF_SESSION_INVALID_EVENT));
  } catch {
    /* no window (tests, SSR) — the storage clear is enough */
  }
};

/**
 * The public key was refused on the direct table path (Phase 3 is in, and this
 * browser holds no token). This browser is not signed in to the database, so
 * treat it exactly like a rejected token: back to the login screen, never a
 * screen of preview data (PatientsView swaps preview data in for ANY failed
 * case query, and "permission denied for table patient_cases" is one).
 */
export const isPublicKeyRefused = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { code?: string; message?: string; status?: number };
  return (
    candidate.code === '42501' ||
    candidate.status === 401 ||
    candidate.status === 403 ||
    /permission denied/i.test(candidate.message ?? '')
  );
};

/** For the direct-table fallback: sign out on a refusal, then rethrow. */
export const directPathError = <E>(error: E): E => {
  if (isPublicKeyRefused(error)) {
    handleInvalidSession();
  }
  return error;
};
